import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Badge, Input, Select, Table, Modal } from '../components/Common';
import FindingChat from '../components/FindingChat';

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];
const STATUSES = ['Open', 'Closed', 'Accepted', 'Retest', 'Reopened'];

const SEVERITY_COLORS = {
  Critical: '#dc2626',
  High: '#ea580c',
  Medium: '#d97706',
  Low: '#2563eb',
  Info: '#6b7280',
};

export default function Vulnerabilities() {
  const { apiFetch, user, hasGroup } = useAuth();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Permission check: can the current user change finding statuses?
  const canChangeStatus = hasGroup('Super Admins') || hasGroup('Engagement Admins') ||
    hasGroup('Penetration Testers') || hasGroup('MSP Penetration Testers') ||
    user?.groups?.includes('Client Admins');

  // Detail modal state
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Initialize filters from URL search params
  const [filter, setFilter] = useState({
    search: searchParams.get('search') || '',
    severity: searchParams.get('severity') || '',
    status: searchParams.get('status') || '',
    client: searchParams.get('client') || '',
    engagementType: searchParams.get('engagementType') || '',
  });

  // Deep link from a notification: /vulnerabilities?finding=<id> opens that
  // finding's detail directly, so the link lands on the item, not just the list.
  // Captured once: the filter-sync effect below rewrites the query string on
  // mount, which would otherwise drop the id before we could use it.
  const deepLinkId = useRef(searchParams.get('finding'));
  const deepLinkOpened = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/findings/all');
      if (res.ok) setFindings(await res.json());
    } catch {}
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  // Open the finding a notification linked to, once the list has loaded.
  useEffect(() => {
    if (!deepLinkId.current || deepLinkOpened.current || loading) return;
    deepLinkOpened.current = true;
    const row = findings.find(f => f.id === deepLinkId.current);
    openFindingDetail(row || { id: deepLinkId.current });
  }, [loading, findings]);

  // Sync filters to URL params
  useEffect(() => {
    const params = {};
    if (filter.severity) params.severity = filter.severity;
    if (filter.status) params.status = filter.status;
    if (filter.search) params.search = filter.search;
    if (filter.client) params.client = filter.client;
    if (filter.engagementType) params.engagementType = filter.engagementType;
    setSearchParams(params, { replace: true });
  }, [filter, setSearchParams]);

  // Fetch full finding details
  const openFindingDetail = async (row) => {
    setDetailLoading(true);
    setSelectedFinding({ ...row, _loading: true });
    try {
      const res = await apiFetch(`/findings/detail/${row.id}`);
      if (res.ok) {
        const full = await res.json();
        setSelectedFinding(full);
      } else {
        setSelectedFinding(row);
      }
    } catch {
      setSelectedFinding(row);
    }
    setDetailLoading(false);
  };

  // Inline status update
  const updateFindingStatus = async (findingId, newStatus) => {
    try {
      const res = await apiFetch(`/findings/${findingId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setFindings(prev => prev.map(f => f.id === findingId ? { ...f, status: newStatus } : f));
      }
    } catch {}
  };

  // Compute unique values for filter dropdowns
  const clients = [...new Set(findings.map(f => f.client_name).filter(Boolean))].sort();
  const engTypes = [...new Set(findings.map(f => f.engagement_type).filter(Boolean))].sort();

  // Apply filters
  const filtered = findings.filter(f => {
    if (filter.severity && f.severity !== filter.severity) return false;
    if (filter.status && f.status !== filter.status) return false;
    if (filter.client && f.client_name !== filter.client) return false;
    if (filter.engagementType && f.engagement_type !== filter.engagementType) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      const searchable = [f.title, f.client_name, f.engagement_name, f.severity, f.status]
        .filter(Boolean).join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  });

  // Summary counts for current filtered view
  const severityCounts = {};
  SEVERITIES.forEach(s => { severityCounts[s] = filtered.filter(f => f.severity === s).length; });
  const openCount = filtered.filter(f => f.status === 'Open').length;

  const clearFilters = () => {
    setFilter({ search: '', severity: '', status: '', client: '', engagementType: '' });
  };

  const hasFilters = filter.severity || filter.status || filter.search || filter.client || filter.engagementType;

  const columns = [
    { key: 'severity', label: 'Severity', render: (v) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: SEVERITY_COLORS[v] || '#6b7280'
        }} />
        <Badge severity={v}>{v}</Badge>
      </div>
    )},
    { key: 'finding_uid', label: 'ID', render: v => v ? <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{v}</span> : '—' },
    { key: 'title', label: 'Vulnerability', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500, marginBottom: 2 }}>{v}</div>
        {row.cvss_score && (
          <span style={{
            fontSize: 11, padding: '1px 6px', borderRadius: 4,
            background: 'var(--bg-input)', border: '1px solid var(--border)',
            color: 'var(--text-secondary)'
          }}>CVSS {row.cvss_score}</span>
        )}
      </div>
    )},
    { key: 'status', label: 'Status', render: (v, row) => canChangeStatus ? (
      <select
        value={v || 'Open'}
        onClick={e => e.stopPropagation()}
        onChange={e => { e.stopPropagation(); updateFindingStatus(row.id, e.target.value); }}
        style={{
          padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--bg-input)', color: 'var(--text)', fontSize: 12,
          cursor: 'pointer', fontWeight: 600, minWidth: 110
        }}>
        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    ) : <Badge>{v}</Badge> },
    { key: 'client_name', label: 'Client', render: v => v || '—' },
    { key: 'engagement_name', label: 'Engagement', render: (v, row) => (
      <div>
        <div style={{ fontSize: 13 }}>{v || '—'}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {row.engagement_type?.replace(' Penetration Test', ' PT').replace('Application', 'App')}
        </div>
      </div>
    )},
    { key: 'created_at', label: 'Found', render: v => v ? new Date(v).toLocaleDateString() : '—' },
  ];

  // Detail section renderer
  const DetailSection = ({ label, value, isCode }) => {
    if (!value) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6, letterSpacing: '0.5px' }}>{label}</div>
        <div style={{
          padding: 12, borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)',
          fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          ...(isCode ? { fontFamily: 'monospace', fontSize: 13 } : {})
        }}>{value}</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Vulnerabilities</h1>
        <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
          {filtered.length} finding{filtered.length !== 1 ? 's' : ''}
          {hasFilters ? ' (filtered)' : ''}
          {openCount > 0 ? ` · ${openCount} open` : ''}
        </span>
      </div>

      {/* Severity summary chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {SEVERITIES.map(sev => {
          const count = severityCounts[sev] || 0;
          const isActive = filter.severity === sev;
          return (
            <button key={sev} onClick={() => setFilter(f => ({ ...f, severity: isActive ? '' : sev }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                borderRadius: 20, border: isActive ? `2px solid ${SEVERITY_COLORS[sev]}` : '1px solid var(--border)',
                background: isActive ? SEVERITY_COLORS[sev] + '18' : 'var(--bg-card)',
                color: count > 0 ? SEVERITY_COLORS[sev] : 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s'
              }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: SEVERITY_COLORS[sev]
              }} />
              {sev} <span style={{ fontWeight: 700 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <Card style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', padding: 16, alignItems: 'flex-end' }}>
        <Input label="" placeholder="Search vulnerabilities..." value={filter.search}
          onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
          style={{ marginBottom: 0, flex: 2, minWidth: 200 }} />
        <Select label=""
          options={[{ value: '', label: 'All Severities' }, ...SEVERITIES.map(s => ({ value: s, label: s }))]}
          value={filter.severity} onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}
          style={{ marginBottom: 0, flex: 1, minWidth: 140 }} />
        <Select label=""
          options={[{ value: '', label: 'All Statuses' }, ...STATUSES.map(s => ({ value: s, label: s }))]}
          value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          style={{ marginBottom: 0, flex: 1, minWidth: 130 }} />
        {(user?.isMsp || user?.groups?.includes('Super Admins')) && (
          <Select label=""
            options={[{ value: '', label: 'All Clients' }, ...clients.map(c => ({ value: c, label: c }))]}
            value={filter.client} onChange={e => setFilter(f => ({ ...f, client: e.target.value }))}
            style={{ marginBottom: 0, flex: 1, minWidth: 150 }} />
        )}
        <Select label=""
          options={[{ value: '', label: 'All Types' }, ...engTypes.map(t => ({ value: t, label: t.replace(' Penetration Test', ' PT').replace('Application', 'App') }))]}
          value={filter.engagementType} onChange={e => setFilter(f => ({ ...f, engagementType: e.target.value }))}
          style={{ marginBottom: 0, flex: 1, minWidth: 150 }} />
        {hasFilters && (
          <button onClick={clearFilters} style={{
            padding: '10px 16px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
            background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer',
            fontSize: 13, whiteSpace: 'nowrap'
          }}>
            ✕ Clear
          </button>
        )}
      </Card>

      {loading ? (
        <Card style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>Loading vulnerabilities...</Card>
      ) : (
        <Table columns={columns} data={filtered}
          onRowClick={row => openFindingDetail(row)} />
      )}

      {/* Vulnerability Detail Modal */}
      <Modal isOpen={!!selectedFinding} onClose={() => setSelectedFinding(null)} title="Vulnerability Details" width={900}>
        {selectedFinding && (
          <div>
            {/* Header with severity + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <span style={{
                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                background: SEVERITY_COLORS[selectedFinding.severity] || '#6b7280'
              }} />
              <Badge severity={selectedFinding.severity}>{selectedFinding.severity}</Badge>
              <Badge>{selectedFinding.status}</Badge>
              {selectedFinding.cvss_score && (
                <span style={{
                  fontSize: 13, padding: '3px 10px', borderRadius: 6,
                  background: 'var(--bg-input)', border: '1px solid var(--border)',
                  fontWeight: 600, color: SEVERITY_COLORS[selectedFinding.severity] || 'var(--text)'
                }}>CVSS {selectedFinding.cvss_score}</span>
              )}
            </div>

            {/* Title */}
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, lineHeight: 1.3 }}>{selectedFinding.title}</h2>

            {/* Meta info row */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
              marginBottom: 20, padding: 14, borderRadius: 10, background: 'var(--bg-input)',
              border: '1px solid var(--border)'
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>Client</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{selectedFinding.client_name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>Engagement</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  <span
                    style={{ cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }}
                    onClick={() => { setSelectedFinding(null); navigate(`/engagements/${selectedFinding.engagement_id}`); }}
                  >{selectedFinding.engagement_name || '—'}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>Type</div>
                <div style={{ fontSize: 14 }}>{selectedFinding.engagement_type || '—'}</div>
              </div>
              {selectedFinding.tester_name && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>Tester</div>
                  <div style={{ fontSize: 14 }}>{selectedFinding.tester_name}</div>
                </div>
              )}
              {selectedFinding.cvss_tags && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>CVSS Vector</div>
                  <div style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{selectedFinding.cvss_tags}</div>
                </div>
              )}
              {selectedFinding.direct_exploitability && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>Direct Exploitability</div>
                  <div style={{ fontSize: 14 }}>{selectedFinding.direct_exploitability}</div>
                </div>
              )}
            </div>

            {detailLoading && (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>Loading full details...</div>
            )}

            {/* Detail sections */}
            {!detailLoading && (
              <>
                <DetailSection label="Vulnerability Details" value={selectedFinding.details} />
                <DetailSection label="Observation" value={selectedFinding.observation} />
                <DetailSection label="Affected Endpoints" value={selectedFinding.affected_endpoints} isCode />
                <DetailSection label="Steps to Reproduce" value={selectedFinding.steps_to_reproduce} />

                {/* Ask the AI about this finding. It can read and reason, and
                    can propose a request — but it cannot send one: that takes
                    the tester pressing Send on a request they have seen and
                    may have edited. */}
                <FindingChat finding={selectedFinding} />

                {/* Evidence Screenshots */}
                {selectedFinding.evidence && selectedFinding.evidence.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 10, letterSpacing: '0.5px' }}>
                      Evidence Screenshots ({selectedFinding.evidence.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {selectedFinding.evidence.map(ev => (
                        <div key={ev.id} style={{
                          padding: 12, borderRadius: 8, background: 'var(--bg-input)',
                          border: '1px solid var(--border)'
                        }}>
                          <div style={{ textAlign: 'center', marginBottom: ev.caption ? 8 : 0 }}>
                            <img
                              src={`/api/findings/${selectedFinding.engagement_id}/${selectedFinding.id}/evidence/${ev.id}/file?token=${sessionStorage.getItem('token')}`}
                              alt={ev.caption || ev.file_name}
                              style={{
                                maxWidth: '100%', maxHeight: 400, borderRadius: 6,
                                cursor: 'pointer', border: '1px solid var(--border)'
                              }}
                              onClick={() => window.open(`/api/findings/${selectedFinding.engagement_id}/${selectedFinding.id}/evidence/${ev.id}/file?token=${sessionStorage.getItem('token')}`, '_blank')}
                              title="Click to view full size"
                            />
                          </div>
                          {ev.caption && (
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', fontStyle: 'italic' }}>
                              {ev.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <DetailSection label="Recommendation" value={selectedFinding.recommendation} />
                <DetailSection label="References" value={selectedFinding.references} />

                {/* Timestamps */}
                <div style={{ display: 'flex', gap: 24, fontSize: 12, color: 'var(--text-secondary)', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <span>Found: {selectedFinding.created_at ? new Date(selectedFinding.created_at).toLocaleString() : '—'}</span>
                  <span>Updated: {selectedFinding.updated_at ? new Date(selectedFinding.updated_at).toLocaleString() : '—'}</span>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button
                    onClick={() => { setSelectedFinding(null); navigate(`/engagements/${selectedFinding.engagement_id}`); }}
                    style={{
                      padding: '10px 20px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                      background: 'var(--bg-input)', color: 'var(--text)', cursor: 'pointer',
                      fontSize: 14, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8
                    }}
                  >
                    🔐 View Engagement
                  </button>
                  <button
                    onClick={() => setSelectedFinding(null)}
                    style={{
                      padding: '10px 20px', borderRadius: 'var(--radius)', border: 'none',
                      background: 'var(--primary)', color: 'white', cursor: 'pointer',
                      fontSize: 14, fontWeight: 500
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
